import { useMemo, useState } from 'react'
import { Brain, RefreshCw, Building2, ChevronDown, ShieldAlert } from 'lucide-react'
import { useTranscriptSummary, type PropertyOption } from '../hooks/useTranscriptSummary'
import { useSelectedPropertyId } from '../../../store/propertyStore'
import type { TranscriptSuggestedFocusArea } from '../../../types/transcript.types'

function formatDateLong(day: string): string {
  const date = new Date(day + 'T00:00:00')
  if (isNaN(date.getTime())) return day
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${date.getDate()} ${months[date.getMonth()]}, ${date.getFullYear()}`
}

function formatGeneratedAt(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  const date = formatDateLong(d.toISOString().slice(0, 10))
  const hours = d.getHours()
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = String(hours % 12 || 12).padStart(2, '0')
  return `${date} · ${displayHours}:${minutes} ${period}`
}

function sentimentLabel(score: number): string {
  if (score <= -0.5) return 'Strongly Negative'
  if (score < -0.1) return 'Slightly Negative'
  if (score <= 0.1) return 'Neutral'
  if (score < 0.5) return 'Slightly Positive'
  return 'Strongly Positive'
}

function Bullet({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <li className="flex gap-2 text-[14px] leading-relaxed text-gray-700">
      <span className="text-gray-400 select-none">•</span>
      <span>
        <span className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">{label}</span>{' '}
        <span>{value}</span>
      </span>
    </li>
  )
}

function PropertySelect({ options, value, onChange }: { options: PropertyOption[]; value: number | undefined; onChange: (id: number) => void }): JSX.Element {
  return (
    <div className="relative inline-flex items-center">
      <Building2 size={14} className="pointer-events-none absolute left-3 text-gray-400" />
      <select
        value={value ?? ''}
        onChange={(e) => { const next = Number(e.target.value); if (!Number.isNaN(next)) onChange(next) }}
        className="h-9 cursor-pointer appearance-none rounded-lg border border-[#E5E7EB] bg-white py-2 pl-9 pr-9 text-[13px] text-gray-700 outline-none transition-colors hover:border-gray-400 focus:border-brand-green"
      >
        <option value="" disabled>Select a property...</option>
        {options.map((opt) => (<option key={opt.id} value={opt.id}>{opt.name}</option>))}
      </select>
      <ChevronDown size={14} className="pointer-events-none absolute right-3 text-gray-400" />
    </div>
  )
}

function SummaryBodySkeleton(): JSX.Element {
  return (
    <div aria-busy="true" data-testid="transcript-summary-skeleton" className="space-y-4">
      <div className="rounded-lg border border-[#E5E7EB] p-5">
        <div className="mb-4 flex items-center gap-2">
          <Brain size={20} className="text-brand-green" />
          <div className="h-6 w-60 animate-pulse rounded bg-gray-100" />
        </div>

        <div className="space-y-3">
          <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-11/12 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-4/5 animate-pulse rounded bg-gray-200" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {['a', 'b', 'c'].map((key) => (
          <div key={key} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="mx-auto h-5 w-20 animate-pulse rounded bg-gray-200" />
            <div className="mt-2 h-8 animate-pulse rounded bg-gray-200" />
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-5">
          <div className="mb-4 h-5 w-44 animate-pulse rounded bg-gray-200" />
          <div className="space-y-3">
            <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
            <div className="h-4 w-10/12 animate-pulse rounded bg-gray-200" />
            <div className="h-4 w-9/12 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-5">
          <div className="mb-4 h-5 w-36 animate-pulse rounded bg-gray-200" />
          <div className="space-y-3">
            <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
            <div className="h-4 w-11/12 animate-pulse rounded bg-gray-200" />
            <div className="h-4 w-8/12 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-100 bg-gray-50 p-5">
        <div className="mb-4 h-5 w-40 animate-pulse rounded bg-gray-200" />
        <div className="space-y-3">
          <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-11/12 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    </div>
  )
}

interface AISummaryProps { day: string }

export function AISummary({ day }: AISummaryProps): JSX.Element {
  const selectedPropertyId = useSelectedPropertyId()
  const [localSelectedPropertyId, setLocalSelectedPropertyId] = useState<number | undefined>(undefined)
  const effectiveSelectedPropertyId = selectedPropertyId ?? localSelectedPropertyId
  const {
    summary, propertyName, loading, regenerating, error,
    regenerate, canRegenerate, needsPropertySelection, propertyOptions,
  } = useTranscriptSummary(day, effectiveSelectedPropertyId)

  const sentiment = summary?.summary_json?.sentiment_overview
  const topCategories = useMemo(() => {
    if (!sentiment?.category_sentiment_scores) return []
    return Object.entries(sentiment.category_sentiment_scores)
      .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a))
      .slice(0, 3)
  }, [sentiment])

  if (needsPropertySelection && !effectiveSelectedPropertyId) {
    return (
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-12 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-green/10">
          <Building2 size={32} className="text-brand-green" />
        </div>
        <h3 className="mb-2 text-[18px] font-semibold text-gray-900">Select a property</h3>
        <p className="mx-auto mb-5 max-w-md text-[13px] text-gray-500">
          Your account has access to multiple properties. Choose one to view its AI Daily Summary.
        </p>
        <PropertySelect options={propertyOptions} value={effectiveSelectedPropertyId} onChange={setLocalSelectedPropertyId} />
      </div>
    )
  }

  if (loading && !summary) {
    return (
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-12 text-center" aria-busy="true" data-testid="transcript-summary-loading">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-green/10">
          <Brain size={32} className="text-brand-green" />
        </div>
        <div className="mx-auto mb-2 h-5 w-44 animate-pulse rounded bg-gray-100" />
        <p className="mx-auto max-w-md text-[13px] text-gray-500">
          First generation can take up to 25 seconds while Gemini analyzes today&apos;s transcripts. Subsequent loads are instant.
        </p>
      </div>
    )
  }

  if (error && !summary) {
    return <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-[14px] text-red-600">{error}</div>
  }

  if (!summary) return <></>

  const generatedAt = formatGeneratedAt(summary.generated_at)
  const isEmpty = summary.transcript_count === 0
  const insights = summary.summary_json.operational_insights
  const securityEvents = summary.summary_json.security_events ?? []
  const patterns = summary.summary_json.patterns_detected ?? []
  const focusAreas = summary.summary_json.suggested_focus_areas ?? []

  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-white p-6">
      <div className="mb-6 flex items-start justify-between gap-4 border-b border-[#E5E7EB] pb-4">
        <div>
          <h2 className="mb-2 text-[22px] font-bold text-gray-900">
            QuickComm: Daily Operational Summary
          </h2>
          <div className="space-y-0.5 text-[13px] text-gray-500">
            {!selectedPropertyId && needsPropertySelection ? (
              <div className="flex items-center gap-2 py-1">
                <span className="font-semibold text-gray-900">Property:</span>
                <PropertySelect options={propertyOptions} value={effectiveSelectedPropertyId} onChange={setLocalSelectedPropertyId} />
              </div>
            ) : (
              propertyName && (
                <div><span className="font-semibold text-gray-900">Property:</span> {propertyName}</div>
              )
            )}
            <div><span className="font-semibold text-gray-900">Date:</span> {formatDateLong(summary.summary_date)}</div>
            {generatedAt && (
              <div><span className="font-semibold text-gray-900">Last updated:</span> {generatedAt}</div>
            )}
          </div>
        </div>
        {canRegenerate && (
          <button onClick={regenerate} disabled={regenerating}
            className="flex items-center gap-2 rounded-lg border border-brand-green/40 px-3.5 py-2 text-[13px] font-semibold text-brand-green transition-colors hover:bg-brand-green/10 disabled:cursor-not-allowed disabled:opacity-60" type="button">
            <RefreshCw size={14} className={regenerating ? 'animate-spin' : undefined} />
            <span>{regenerating ? 'Regenerating...' : 'Regenerate'}</span>
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-600">{error}</div>
      )}

      {regenerating && (
        <div className="mb-4 rounded-lg border border-brand-green/20 bg-brand-green/5 p-3 text-[13px] text-brand-green">
          Regenerating summary with the latest transcript data.
        </div>
      )}

      {regenerating ? (
        <SummaryBodySkeleton />
      ) : isEmpty ? (
        <div className="py-8 text-center">
          <p className="text-[14px] text-gray-500">No voice activity recorded on {formatDateLong(summary.summary_date)}.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* AI Operational Insights */}
          <div className="rounded-lg border border-[#E5E7EB] p-5">
            <div className="mb-4 flex items-center gap-2">
              <Brain size={20} className="text-brand-green" />
              <h3 className="text-[18px] font-semibold text-gray-900">AI Operational Insights</h3>
            </div>

            <ul className="space-y-1.5">
              {insights.peak_hours && <Bullet label="Peak Operational Load" value={insights.peak_hours} />}
              {insights.most_common_issue_category && <Bullet label="Most Common Issue: " value={insights.most_common_issue_category} />}
              {insights.workload_observations && <Bullet label="Department with Highest Workload: " value={insights.workload_observations} />}
              {insights.frustration_indicators && <Bullet label="Guest Frustration Indicators: " value={insights.frustration_indicators} />}

              {/* Sentiment Intelligence sub-section */}
              {sentiment && (
          <div className="mt-4 border-t border-[#E5E7EB] pt-4">
            <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">Sentiment Intelligence</h4>
            <div className="grid grid-cols-3 gap-3 text-center text-sm">
              <div className="rounded-lg bg-green-50 p-2">
                <p className="text-lg font-bold text-green-700">{sentiment.positive_percent}%</p>
                <p className="text-xs text-green-600">Positive</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-2">
                <p className="text-lg font-bold text-gray-700">{sentiment.neutral_percent}%</p>
                <p className="text-xs text-gray-500">Neutral</p>
              </div>
              <div className="rounded-lg bg-red-50 p-2">
                <p className="text-lg font-bold text-red-700">{sentiment.negative_percent}%</p>
                <p className="text-xs text-red-600">Negative</p>
              </div>
            </div>
            {topCategories.length > 0 && (
              <div className="mt-3 space-y-1">
                {topCategories.map(([cat, score]) => (
                  <div key={cat} className="flex items-center justify-between text-xs text-gray-600">
                    <span className="capitalize">{cat}</span>
                    <span className={score >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {sentimentLabel(score)} ({score.toFixed(2)})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
            </ul>

            {/* Patterns Detected */}
            {patterns.length > 0 && (
              <div className="mt-4">
                <span className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">Pattern Detected:</span>
                <ul className="space-y-1.5">
                  {patterns.map((p, i) => (
                    <li key={i} className="flex gap-2 text-[14px] leading-relaxed text-gray-700">
                      <span className="text-gray-400 select-none">•</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Suggested Focus Areas */}
            {focusAreas.length > 0 && (
              <div className="mt-4">
                <span className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">Suggested Focus Area:</span>
                <ul className="space-y-2">
                  {focusAreas.map((area: TranscriptSuggestedFocusArea, i: number) => (
                    <li key={i} className="flex gap-2 text-[14px] ">
                      <span className="text-gray-400 select-none">•</span>
                      <div>
                        <div className="font-semibold text-gray-900">{area.area}</div>
                        {area.expected_impact && (
                          <div className="text-gray-500">{area.expected_impact}</div>
                        )}
                        {area.estimated_change_percent != null && (
                          <div className="mt-1 flex items-center gap-2">
                            <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${
                              area.estimated_change_percent <= 0
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {area.estimated_change_percent > 0 ? '+' : ''}{Math.round(area.estimated_change_percent)}%
                            </span>
                            {area.basis && (
                              <span className="max-w-s text-xs text-gray-400" title={area.basis}>
                                {area.basis}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Security Events */}
          {securityEvents.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50/50 p-5">
              <div className="mb-4 flex items-center gap-2">
                <ShieldAlert size={20} className="text-red-600" />
                <h3 className="text-[18px] font-semibold text-red-700">Security Events</h3>
              </div>
              <div className="space-y-3">
                {securityEvents.map((se, i) => (
                  <div key={i} className="rounded-lg border border-red-200 bg-white p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <ShieldAlert size={16} className="flex-shrink-0 text-red-600" />
                      <span className="text-[14px] font-semibold text-red-700">{se.event}</span>
                    </div>
                    <div className="ml-6 space-y-2 text-[13px] leading-relaxed">
                      <div><span className="font-semibold text-gray-500">Cause: </span><span className="text-gray-700">{se.cause}</span></div>
                      <div><span className="font-semibold text-gray-500">Impact: </span><span className="text-gray-700">{se.impact}</span></div>
                      <div><span className="font-semibold text-gray-500">Resolution: </span><span className="text-gray-700">{se.resolution}</span></div>
                      {se.follow_ups.length > 0 && (
                        <div>
                          <span className="mb-1 block text-[13px] font-semibold text-gray-500">Follow-ups:</span>
                          <ul className="space-y-1">
                            {se.follow_ups.map((fu, j) => (
                              <li key={j} className="flex gap-2 text-[13px] leading-snug text-gray-700">
                                <span className="text-red-400/60 select-none">•</span>
                                <span>{fu}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
